import { Box, Button, Checkbox, FormControlLabel, FormGroup, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { EventEditionDetails } from "../../../common/types";
import colors from "../../../common/styles/colors";

const useStyles = makeStyles({
  container: {
    marginTop: 16,
    marginBottom: 16
  },
  title: {
    fontWeight: 600,
    marginBottom: 8,
    fontSize: 14
  },
  description: {
    color: colors.lightGrey10,
    marginBottom: 16,
    fontSize: 14
  },
  group: {
    gap: 8
  },
  row: {
    border: `1px solid ${colors.lightGrey11}`,
    borderRadius: 8,
    padding: 14
  },
  rowLabel: {
    margin: 0,
    width: "100%"
  },
  rowLabelText: {
    marginLeft: 8
  },
  saveButton: {
    marginTop: 16,
    marginBottom: 8
  }
});

type Props = {
  colocatedShows: EventEditionDetails[];
  selectedShows: EventEditionDetails[];
  onToggleShow: (show: EventEditionDetails) => void;
  loading?: boolean;
  eventEditionName?: string;
  onSaveChanges?: () => void;
  saveChangesDisabled?: boolean;
  saveChangesLabel?: string;
};

export const ColocatedShows = ({
  colocatedShows,
  selectedShows,
  onToggleShow,
  loading,
  eventEditionName,
  onSaveChanges,
  saveChangesDisabled,
  saveChangesLabel
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation("common");

  if (loading || !colocatedShows.length) {
    return null;
  }

  const editionNameText = eventEditionName || t("settings.eventEdition");
  const getShowLabel = (show: EventEditionDetails) => show.name || show.gbsCode || show.id;
  const isShowSelected = (showId: string) => selectedShows.some(item => item.id === showId);

  return (
    <Box className={classes.container} data-testid="colocated-shows-container">
      <Typography className={classes.title}>{t("kioskPrintSettings.colocatedShows.title")}</Typography>
      <Typography className={classes.description}>
        {t("kioskPrintSettings.colocatedShows.description", { eventEditionName: editionNameText })}
      </Typography>
      <FormGroup className={classes.group}>
        {colocatedShows.map(show => {
          const label = getShowLabel(show);
          const checked = isShowSelected(show.id);

          return (
            <Box key={show.id} className={classes.row}>
              <FormControlLabel
                className={classes.rowLabel}
                control={<Checkbox checked={checked} onChange={() => onToggleShow(show)} />}
                label={<span className={classes.rowLabelText}>{label}</span>}
              />
            </Box>
          );
        })}
      </FormGroup>
      {!!onSaveChanges && (
        <Button variant="outlined" className={classes.saveButton} onClick={onSaveChanges} disabled={saveChangesDisabled}>
          {saveChangesLabel}
        </Button>
      )}
    </Box>
  );
};
